"use client";

import { Trans } from "@lingui/react/macro";

import { Check, ChevronDown } from "lucide-react";
import { useMemo, useState } from "react";
import { cn } from "../../lib/utils";
import { Button } from "../ui/button";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "../ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Skeleton } from "../ui/skeleton";

export type LocationComboboxOption = { id: string; label: string };

export type LocationComboboxProps = {
	/** Applied to the trigger button so a `<label htmlFor>` can point at it. */
	id?: string;
	placeholder: string;
	searchPlaceholder?: string;
	options: LocationComboboxOption[];
	value: string | null;
	disabled?: boolean;
	onSelect: (id: string) => void;
	/**
	 * When true, a query that doesn't match any option surfaces a "Use '…'"
	 * row that selects the raw query string. Used by the city picker so
	 * countries with sparse coverage in `country-state-city` aren't a dead
	 * end.
	 */
	allowFreeForm?: boolean;
	/**
	 * Shows skeleton rows in place of options while a lazy dataset loads.
	 * The free-form row (when enabled) stays available during loading.
	 */
	loading?: boolean;
};

const FREE_FORM_MAX_LENGTH = 120;

export const LocationCombobox = ({
	id,
	placeholder,
	searchPlaceholder,
	options,
	value,
	disabled,
	onSelect,
	allowFreeForm = false,
	loading = false,
}: LocationComboboxProps) => {
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState("");

	const lowerQuery = query.trim().toLowerCase();
	const filtered = useMemo(
		() =>
			lowerQuery
				? options.filter((o) => o.label.toLowerCase().includes(lowerQuery))
				: options,
		[options, lowerQuery],
	);

	const selectedLabel = useMemo(
		// For free-form pickers the selected value may not be in `options` —
		// fall through to the raw value so the trigger label still reads
		// correctly after a custom entry.
		() => options.find((o) => o.id === value)?.label ?? value ?? null,
		[options, value],
	);

	const trimmedQuery = query.trim();
	const exactMatch = trimmedQuery
		? options.some((o) => o.label.toLowerCase() === trimmedQuery.toLowerCase())
		: false;
	const showFreeFormOption =
		allowFreeForm &&
		trimmedQuery.length > 0 &&
		trimmedQuery.length <= FREE_FORM_MAX_LENGTH &&
		!exactMatch;

	return (
		<Popover
			open={open}
			onOpenChange={setOpen}
		>
			<PopoverTrigger asChild>
				<Button
					id={id}
					type="button"
					variant="unstyled"
					rounding="md"
					size="default"
					disabled={disabled}
					aria-expanded={open}
					className={cn(
						"flex w-full cursor-pointer items-center justify-between border border-klarify-neutral-200 bg-white px-3 py-2 text-left font-normal text-sm shadow-sm transition-colors hover:border-klarify-neutral-300 hover:bg-klarify-neutral-50",
						selectedLabel
							? "text-klarify-neutral-900"
							: "text-klarify-neutral-500",
					)}
				>
					<span className="truncate">{selectedLabel ?? placeholder}</span>
					<ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
				</Button>
			</PopoverTrigger>
			<PopoverContent
				className="w-(--radix-popover-trigger-width) p-0"
				align="start"
			>
				<Command shouldFilter={false}>
					<CommandInput
						placeholder={searchPlaceholder ?? placeholder}
						value={query}
						onValueChange={setQuery}
					/>
					<CommandList>
						{loading ? (
							<div className="space-y-1 p-1">
								<Skeleton className="h-8 w-full" />
								<Skeleton className="h-8 w-full" />
								<Skeleton className="h-8 w-full" />
							</div>
						) : !showFreeFormOption && filtered.length === 0 ? (
							<CommandEmpty>
								<span className="block px-3 py-6 text-center text-klarify-neutral-500 text-sm">
									<Trans>No matches</Trans>
								</span>
							</CommandEmpty>
						) : null}
						<CommandGroup>
							{filtered.map((opt) => (
								<CommandItem
									key={opt.id}
									value={opt.id}
									onSelect={() => {
										onSelect(opt.id);
										setOpen(false);
										setQuery("");
									}}
									className="cursor-pointer"
								>
									<span className="flex-1 truncate">{opt.label}</span>
									{value === opt.id ? (
										<Check className="ml-2 h-4 w-4 text-klarify-forest-700" />
									) : null}
								</CommandItem>
							))}
							{showFreeFormOption ? (
								<CommandItem
									value={`__freeform__${trimmedQuery}`}
									onSelect={() => {
										onSelect(trimmedQuery);
										setOpen(false);
										setQuery("");
									}}
									className="cursor-pointer text-klarify-neutral-600"
								>
									<span className="flex-1 truncate">
										<Trans>Use</Trans>{" "}
										<span className="font-medium text-klarify-neutral-900">
											“{trimmedQuery}”
										</span>
									</span>
								</CommandItem>
							) : null}
						</CommandGroup>
					</CommandList>
				</Command>
			</PopoverContent>
		</Popover>
	);
};
LocationCombobox.displayName = "LocationCombobox";
